import { Suspense, lazy, useState } from 'react'
import { Link, Routes, Route } from 'react-router-dom'
import { ShieldCheck } from 'lucide-react'
import Header from './components/layout/Header'
import Footer from './components/layout/Footer'
import AwardsBand from './components/AwardsBand'
import SocialRail from './components/layout/SocialRail'
import MobileContactBar from './components/layout/MobileContactBar'
import AccessibilityWidget from './components/layout/AccessibilityWidget'
import ScrollToTop from './components/layout/ScrollToTop'
import RouteScrollTop from './components/layout/RouteScrollTop'
import Home from './pages/Home'
// Route-level code splitting (SEO P7): Leaflet/markercluster and the heavier
// pages must not load on `/`. data-suspense-fallback marks the shell so the
// prerender script waits for the real chunk before capturing HTML.
const Listings = lazy(() => import('./pages/Listings'))
const About = lazy(() => import('./pages/About'))
const Contact = lazy(() => import('./pages/Contact'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Accessibility = lazy(() => import('./pages/Accessibility'))
const Sitemap = lazy(() => import('./pages/Sitemap'))
const AreasIndex = lazy(() => import('./pages/AreasIndex'))
const AreaPage = lazy(() => import('./pages/AreaPage'))
const Sell = lazy(() => import('./pages/Sell'))
const Buy = lazy(() => import('./pages/Buy'))
const Faq = lazy(() => import('./pages/Faq'))
const NotFound = lazy(() => import('./pages/NotFound'))
import { getArea } from './data/areas'
import { usePageTracking } from './lib/analytics'

const PRIVACY_CHOICE_KEY = 'debeach-privacy-choice'
const PRIVACY_CHOICE_EVENT = 'debeach:privacy-choice'
type PrivacyChoice = 'accepted' | 'declined' | null

const readPrivacyChoice = (): PrivacyChoice => {
  if (typeof window === 'undefined') return null
  try {
    const choice = localStorage.getItem(PRIVACY_CHOICE_KEY)
    return choice === 'accepted' || choice === 'declined' ? choice : null
  } catch {
    return null
  }
}

function PrivacyNotice() {
  const [choice, setChoice] = useState<PrivacyChoice>(readPrivacyChoice)
  if (choice) return null

  const saveChoice = (next: Exclude<PrivacyChoice, null>) => {
    try {
      localStorage.setItem(PRIVACY_CHOICE_KEY, next)
    } catch {
      void 0
    }
    setChoice(next)
    window.dispatchEvent(new CustomEvent(PRIVACY_CHOICE_EVENT, { detail: { choice: next } }))
  }

  return (
    <aside data-privacy-notice role="dialog" aria-label="Privacy choices" className="fixed inset-x-2 bottom-[max(0.5rem,env(safe-area-inset-bottom))] z-[85] overflow-hidden rounded-full border border-white/15 bg-primary-ink/95 text-white shadow-[0_18px_55px_rgba(6,20,31,0.4)] backdrop-blur-xl md:inset-x-auto md:bottom-auto md:right-5 md:top-20 md:w-auto md:max-w-2xl">
      <span className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent-gold to-transparent" aria-hidden="true" />
      <div className="flex h-[58px] items-center gap-2 px-2.5 md:gap-3 md:px-3">
        <span className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-gold/15 text-accent-gold md:flex" aria-hidden="true"><ShieldCheck size={17} /></span>
        <p className="min-w-0 flex-1 truncate font-sans text-[10px] text-white/65 md:text-[11px]"><span className="font-semibold uppercase tracking-[0.16em] text-accent-gold">Privacy</span><span> · Optional analytics<span className="hidden md:inline"> help improve the site. We never sell your information.</span></span></p>
        <div className="flex shrink-0 items-center gap-1.5">
          <Link to="/privacy" aria-label="Read the privacy policy" className="inline-flex min-h-11 items-center px-1 font-sans text-[8px] font-medium uppercase tracking-[0.1em] text-white/60 transition-colors hover:text-white md:px-2 md:text-[9px]">Policy</Link>
          <button type="button" onClick={() => saveChoice('declined')} aria-label="Decline optional analytics" className="inline-flex min-h-11 min-w-10 items-center justify-center rounded-full border border-white/20 px-2 font-sans text-[8px] font-medium uppercase tracking-[0.1em] text-white/80 transition-colors hover:border-white/45 hover:text-white md:px-3 md:text-[9px]">No</button>
          <button type="button" data-touch-accent onClick={() => saveChoice('accepted')} aria-label="Allow optional analytics" className="inline-flex min-h-11 items-center rounded-full bg-accent-gold px-3 font-sans text-[8px] font-semibold uppercase tracking-[0.1em] text-primary-ink transition-colors hover:bg-white md:px-4 md:text-[9px]">Allow</button>
        </div>
      </div>
    </aside>
  )
}

function AreaRoute() {
  // filter(Boolean) drops empty segments — Pages redirects /areas/x to
  // /areas/x/ (prerendered dir index), and a naive split('/').pop() then
  // yields '' → false NotFound on every hard navigation to a town page.
  const slug = window.location.pathname.split('/').filter(Boolean).pop() ?? ''
  const area = getArea(slug)
  return area ? <AreaPage area={area} /> : <NotFound />
}

function App() {
  usePageTracking()
  return (
    <div className="min-h-screen bg-neutral-charcoal text-neutral-slate font-sans">
      <RouteScrollTop />
      <Header />
      <SocialRail />
      <main className="flex-grow">
        <Suspense fallback={<div data-suspense-fallback className="min-h-screen bg-neutral-charcoal" />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/listings" element={<Listings />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/areas" element={<AreasIndex />} />
          <Route path="/areas/:slug" element={<AreaRoute />} />
          <Route path="/sell" element={<Sell />} />
          <Route path="/buy" element={<Buy />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
      <AwardsBand />
      <Footer />
      <AccessibilityWidget />
      <MobileContactBar />
      <ScrollToTop />
      <PrivacyNotice />
    </div>
  )
}

export default App
